import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type { InvoiceTemplateCreateInput } from '@/lib/invoicing/types';

// =============================================
// Invoice Template CRUD
// =============================================

export default withAuth(async (req, res, _user) => {
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res);
      case 'POST':
        return handleCreate(req, res);
      case 'PUT':
        return handleUpdate(req, res);
      case 'DELETE':
        return handleDelete(req, res);
      default:
        return res.status(405).json({ error: 'Método no permitido' });
    }
  } catch (error: any) {
    console.error('[API invoice-templates] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});

// =============================================
// GET: List templates for an empresa
// =============================================

async function handleGet(req: NextApiRequest, res: NextApiResponse) {
  const { empresa_id } = req.query;

  if (!empresa_id) {
    return res.status(400).json({ error: 'empresa_id es requerido' });
  }

  const { data, error } = await supabaseAdmin
    .from('invoice_templates')
    .select('*')
    .eq('empresa_id', empresa_id as string)
    .order('es_default', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[API invoice-templates] GET error:', error);
    return res.status(500).json({ error: `Error al listar plantillas: ${error.message}` });
  }

  return res.status(200).json({ data: data ?? [] });
}

// =============================================
// POST: Create a new template
// =============================================

async function handleCreate(req: NextApiRequest, res: NextApiResponse) {
  const body: InvoiceTemplateCreateInput = req.body;

  if (!body.empresa_id) return res.status(400).json({ error: 'empresa_id es requerido' });
  if (!body.nombre) return res.status(400).json({ error: 'nombre es requerido' });

  // If marking as default, unset existing defaults first
  if (body.es_default) {
    await supabaseAdmin
      .from('invoice_templates')
      .update({ es_default: false })
      .eq('empresa_id', body.empresa_id)
      .eq('es_default', true);
  }

  const templateData = {
    empresa_id: body.empresa_id,
    nombre: body.nombre,
    logo_url: body.logo_url ?? null,
    color_primario: body.color_primario ?? '#1a56db',
    color_secundario: body.color_secundario ?? '#374151',
    pie_pagina: body.pie_pagina ?? null,
    datos_bancarios: body.datos_bancarios ?? null,
    condiciones_pago: body.condiciones_pago ?? null,
    es_default: body.es_default ?? false,
    html_template: body.html_template ?? null,
  };

  const { data, error } = await supabaseAdmin
    .from('invoice_templates')
    .insert(templateData)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-templates] POST error:', error);
    return res.status(500).json({ error: `Error al crear plantilla: ${error.message}` });
  }

  return res.status(201).json({ success: true, data });
}

// =============================================
// PUT: Update a template
// =============================================

async function handleUpdate(req: NextApiRequest, res: NextApiResponse) {
  const { id, ...updates } = req.body as Partial<InvoiceTemplateCreateInput> & { id: string };

  if (!id) return res.status(400).json({ error: 'id es requerido' });

  // Verify template exists
  const { data: existing, error: fetchError } = await supabaseAdmin
    .from('invoice_templates')
    .select('id, empresa_id')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    return res.status(404).json({ error: 'Plantilla no encontrada' });
  }

  // If marking as default, unset existing defaults first
  if (updates.es_default) {
    await supabaseAdmin
      .from('invoice_templates')
      .update({ es_default: false })
      .eq('empresa_id', existing.empresa_id)
      .eq('es_default', true);
  }

  // Build update data
  const updateData: Record<string, unknown> = {};
  const allowedFields = [
    'nombre', 'logo_url', 'color_primario', 'color_secundario',
    'pie_pagina', 'datos_bancarios', 'condiciones_pago', 'es_default', 'html_template',
  ];

  for (const field of allowedFields) {
    if ((updates as Record<string, unknown>)[field] !== undefined) {
      updateData[field] = (updates as Record<string, unknown>)[field];
    }
  }

  if (Object.keys(updateData).length === 0) {
    return res.status(400).json({ error: 'No se proporcionaron campos para actualizar' });
  }

  const { data, error } = await supabaseAdmin
    .from('invoice_templates')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-templates] PUT error:', error);
    return res.status(500).json({ error: `Error al actualizar plantilla: ${error.message}` });
  }

  return res.status(200).json({ success: true, data });
}

// =============================================
// DELETE: Remove a template
// =============================================

async function handleDelete(req: NextApiRequest, res: NextApiResponse) {
  const { id } = req.body;

  if (!id) return res.status(400).json({ error: 'id es requerido' });

  // Verify template exists
  const { data: existing, error: fetchError } = await supabaseAdmin
    .from('invoice_templates')
    .select('id, es_default')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    return res.status(404).json({ error: 'Plantilla no encontrada' });
  }

  if (existing.es_default) {
    return res.status(400).json({ error: 'No se puede eliminar la plantilla por defecto. Establezca otra como default primero.' });
  }

  const { error } = await supabaseAdmin
    .from('invoice_templates')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[API invoice-templates] DELETE error:', error);
    return res.status(500).json({ error: `Error al eliminar plantilla: ${error.message}` });
  }

  return res.status(200).json({ success: true, message: 'Plantilla eliminada' });
}
